import { identityVerificationDashboardHttp } from './identityVerificationDashboardClient';

const DASH = '/api/identity-verification/v1/admin/dashboard';

export type IdentitySessionSortBy =
    | 'CREATED_AT_DESC'
    | 'CREATED_AT_ASC'
    | 'UPDATED_AT_DESC'
    | 'UPDATED_AT_ASC';

export interface IdentityDashboardSummary {
    totals: {
        sessions: number;
        customers: number;
        deduplication_sessions: number;
        uploaded_images: number;
    };
    sessionStatusCounts: Record<string, number>;
    verificationRouteCounts: Record<string, number>;
    vendorCounts: Record<string, number>;
    deduplicationStatusCounts: Record<string, number>;
    customerStatusCounts: Record<string, number>;
    statusLegend: Record<string, string>;
    routeLegend: Record<string, string>;
}

export interface IdentitySessionListItem {
    iv_verification_id: string;
    internal_id: string;
    vendor: string;
    verification_route: string | null;
    status: string;
    email: string | null;
    phone_number: string | null;
    ccid: string | null;
    document_type: string | null;
    issuing_province: string | null;
    issuing_country: string | null;
    expiry_date: string | null;
    issued_date: string | null;
    verification_date: string | null;
    has_ocr: boolean;
    has_checks: boolean;
    has_dedup: boolean;
    selfie_count: number;
    has_id_front: boolean;
    has_id_back: boolean;
    image_upload_count: number;
    created_at: string;
    updated_at: string;
}

export interface IdentitySessionListResponse {
    total: number;
    limit: number;
    offset: number;
    items: IdentitySessionListItem[];
}

export interface IdentitySessionDetail {
    session: {
        iv_verification_id: string;
        internal_id: string;
        vendor: string;
        verification_route: string | null;
        status: string;
        email: string | null;
        phone_number: string | null;
        ccid: string | null;
        id_front_path: string | null;
        id_back_path: string | null;
        selfie_paths: string[] | null;
        vendor_applicant_id: string | null;
        document_type: string | null;
        issuing_province: string | null;
        issuing_country: string | null;
        expiry_date: string | null;
        issued_date: string | null;
        verification_date: string | null;
        ocr_data: Record<string, unknown> | null;
        checks_result: Record<string, unknown> | null;
        raw_idv_response: Record<string, unknown> | null;
        vendor_evidence_processing: Record<string, unknown> | null;
        biometric_vector: number[] | null;
        created_at: string;
        updated_at: string;
    };
    dedupSessions: Array<{
        dv_verification_id: string;
        iv_verification_id: string;
        internal_id: string;
        status: string;
        deduplication_result: Record<string, unknown> | null;
        deduplication_input_audit: Record<string, unknown> | null;
        created_at: string;
        updated_at: string;
    }>;
    customer: {
        ccid: string;
        internal_id: string;
        status: string;
        iv_verification_id: string;
        email: string | null;
        phone_number: string | null;
        first_name: string | null;
        last_name: string | null;
        date_of_birth: string | null;
        residential_address: string | null;
        created_at: string;
        updated_at: string;
    } | null;
    uploadedImages: Array<{
        id: string;
        iv_verification_id: string;
        image_type: string;
        sequence_index: number;
        file_path: string;
        content_type: string;
        size_bytes: number;
        sha256_hash: string;
        original_filename: string | null;
        storage_kind: string;
        created_at: string;
    }>;
}

export type JourneyStepStatus =
    | 'PASS'
    | 'FAIL'
    | 'SKIPPED'
    | 'PENDING'
    | 'NOT_RUN'
    | 'INFO';

export interface JourneyStep {
    key: string;
    label: string;
    status: JourneyStepStatus;
    summary: string;
    details?: Record<string, unknown> | null;
}

export interface JourneyTierStatus {
    tier: string;
    label: string;
    hit: boolean;
    evaluated: boolean;
    details: Record<string, unknown>;
}

export interface DedupPolicyTier {
    tier: string;
    label: string;
    weight: number;
    logic_config: Record<string, unknown>;
}

export interface DedupPolicySummary {
    threshold: number;
    description: string | null;
    tiers: DedupPolicyTier[];
}

export interface IdentityJourneyRow {
    iv_verification_id: string;
    internal_id: string;
    ccid: string | null;
    vendor: string;
    verification_route: string | null;
    session_status: string;
    email: string | null;
    phone_number: string | null;
    document_type: string | null;
    issuing_country: string | null;
    issuing_province: string | null;
    created_at: string;
    updated_at: string;
    dedup: {
        dv_verification_id: string;
        status: string;
        is_duplicate: boolean | null;
        score: number | null;
        threshold: number | null;
        message: string | null;
        matched_tiers: string[];
        tier_evaluation: JourneyTierStatus[];
        candidate_match: Record<string, unknown> | null;
        ai_interaction: Record<string, unknown> | null;
        created_at: string;
    } | null;
    steps: JourneyStep[];
    customer: {
        ccid: string;
        internal_id: string;
        status: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        phone_number: string | null;
        date_of_birth: string | null;
        residential_address: string | null;
        created_at: string;
        updated_at: string;
    } | null;
}

export interface IdentityJourneyResponse {
    total: number;
    limit: number;
    offset: number;
    items: IdentityJourneyRow[];
    policy: DedupPolicySummary;
}

export type IdentityJourneyStatusGroup =
    | 'NO_MITEK'
    | 'MITEK_CALLED'
    | 'CUSTOMER_CREATED'
    | 'NO_CUSTOMER';

export interface IdentityJourneyParams {
    limit?: number;
    offset?: number;
    statusGroup?: IdentityJourneyStatusGroup;
    tierHit?: string;
    ivVerificationIdContains?: string;
    ccidContains?: string;
    emailContains?: string;
}

export type MitekJudgement = 'AUTHENTIC' | 'FAILED' | 'UNDETERMINED' | 'OTHER';

export interface MitekVerificationCheck {
    name: string;
    judgement: string;
    judgementGroup: MitekJudgement;
    probability: number | null;
    verificationType: number | string | null;
    documentId: string | null;
    notifications: Record<string, string>;
}

export interface MitekImageProcessing {
    imageId: string | null;
    processingStatus: string | null;
    classification: { mdsid?: string | null; imageType?: string | null } | null;
    processingReasons: Record<string, string>;
    extractedFields: Array<{ key: string; value: string }>;
}

export interface MitekDossierBreakdown {
    dossierId: string | null;
    version: string | null;
    createdDateTime: string | null;
    customerReferenceId: string | null;
    overallProcessingStatus: string | null;
    findings: {
        authenticated: boolean | null;
        probability: number | null;
        passCount: number;
        failCount: number;
        undeterminedCount: number;
        otherCount: number;
    };
    verifications: MitekVerificationCheck[];
    extractedSummary: Array<{ key: string; value: string }>;
    images: MitekImageProcessing[];
}

export interface IdentityLookupRow {
    iv_verification_id: string;
    internal_id: string;
    ccid: string | null;
    /** 1-based attempt within the application; null on legacy rows. */
    verification_attempt_number?: number | null;
    vendor: string;
    verification_route: string | null;
    session_status: string;
    email: string | null;
    phone_number: string | null;
    document_type: string | null;
    issuing_country: string | null;
    issuing_province: string | null;
    expiry_date: string | null;
    issued_date: string | null;
    verification_date: string | null;
    ocr_data: Record<string, unknown> | null;
    /** Attempt-local terminal evidence; retained even when a later attempt succeeds. */
    checks_result?: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
    mitekCalled: boolean;
    dossier: MitekDossierBreakdown | null;
    evidenceProcessingPrimary: {
        primaryCode: string | null;
        primaryMessage: string | null;
        imageFailures: Array<{
            imageId?: string;
            processingStatus?: string;
            reasons: Record<string, string>;
        }>;
        reasonsFlat: Record<string, string>;
    } | null;
    latestDedup: {
        dv_verification_id: string;
        status: string;
        isDuplicate: boolean | null;
        score: number | null;
        threshold: number | null;
        message: string | null;
        matched_tiers: string[];
        tier_evaluation: JourneyTierStatus[];
        candidate_match: Record<string, unknown> | null;
        closest_customers: Record<string, unknown>[];
        ai_interaction: Record<string, unknown> | null;
        deduplication_input_audit: Record<string, unknown> | null;
        candidates_evaluated: number | null;
        matched_hit_customer: {
            ccid: string;
            internal_id: string;
            status: string;
            first_name: string | null;
            last_name: string | null;
            email: string | null;
            phone_number: string | null;
            date_of_birth: string | null;
            residential_address: string | null;
            created_at: string;
            updated_at: string;
            /** On-file selfie for Show — load via access URL / content on click. */
            selfie?: IdentityNeighborhoodImageRef | null;
            /** On-file ID document for Show. */
            document?: IdentityNeighborhoodImageRef | null;
        } | null;
        created_at: string;
    } | null;
    customer: {
        ccid: string;
        internal_id: string;
        status: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        phone_number: string | null;
        date_of_birth: string | null;
        residential_address: string | null;
        created_at: string;
        updated_at: string;
    } | null;
    uploadedImages: IdentityLookupUploadedImage[];
    /** Side-by-side selfie pair for returning 1:1 or dedup biometric hits. */
    biometricMatchComparison?: BiometricMatchComparison | null;
}

export interface IdentityLookupUploadedImage {
    id: string;
    image_type: 'selfie' | 'id_front' | 'id_back';
    sequence_index: number;
    file_path: string;
    original_filename: string | null;
    content_type: string;
    size_bytes: number;
    sha256_hash: string;
    storage_kind: string;
    created_at: string;
    iv_verification_id?: string | null;
    /** Short-lived Azure SAS for direct browser load (null for filesystem / unavailable). */
    preview_url?: string | null;
    preview_url_expires_at?: string | null;
}

/** Compact image handle on match-neighborhood rows (load on Show click). */
export interface IdentityNeighborhoodImageRef {
    uploaded_image_id: string | null;
    file_path: string;
    image_type: string;
    iv_verification_id: string | null;
    storage_kind: string | null;
}

export interface BiometricMatchImageSide {
    uploaded_image_id: string | null;
    file_path: string;
    image_type: string;
    iv_verification_id: string | null;
    label: string;
    storage_kind: string | null;
    /** Short-lived Azure SAS — prefer over content proxy. */
    preview_url?: string | null;
    preview_url_expires_at?: string | null;
}

/** Probe vs reference selfie for returning 1:1 or dedup biometric hits. */
export interface BiometricMatchComparison {
    kind: 'RETURNING_SELFIE_1TO1' | 'DEDUP_BIOMETRIC_HIT';
    passed: boolean | null;
    distance: number | null;
    threshold: number | null;
    probe: BiometricMatchImageSide | null;
    reference: BiometricMatchImageSide | null;
}

export interface StorageAccessUrlResponse {
    url: string;
    expiresAt: string;
    expiresInSeconds: number;
    contentType: string;
    fileName: string;
}

export interface IdentityLookupResponse {
    criteria: { applicationId: string | null; ccid: string | null; email: string | null; phone: string | null };
    items: IdentityLookupRow[];
}

export interface IdentityLookupParams {
    applicationId?: string;
    ccid?: string;
    email?: string;
    phone?: string;
}

export interface IdentitySessionListParams {
    limit?: number;
    offset?: number;
    status?: string;
    verification_route?: string;
    vendor?: string;
    applicationId?: string;
    ccidContains?: string;
    emailContains?: string;
    phoneContains?: string;
    ivVerificationIdContains?: string;
    internalIdContains?: string;
    createdFrom?: string;
    createdTo?: string;
    sortBy?: IdentitySessionSortBy;
}

export const identityVerificationDashboardApi = {
    async summary(): Promise<IdentityDashboardSummary> {
        const res = await identityVerificationDashboardHttp.get<IdentityDashboardSummary>(
            `${DASH}/summary`,
        );
        return res.data;
    },

    async list(params: IdentitySessionListParams): Promise<IdentitySessionListResponse> {
        const res = await identityVerificationDashboardHttp.get<IdentitySessionListResponse>(
            `${DASH}/sessions`,
            { params },
        );
        return res.data;
    },

    async detail(ivVerificationId: string): Promise<IdentitySessionDetail> {
        const res = await identityVerificationDashboardHttp.get<IdentitySessionDetail>(
            `${DASH}/sessions/${encodeURIComponent(ivVerificationId)}`,
        );
        return res.data;
    },

    async policy(): Promise<DedupPolicySummary> {
        const res = await identityVerificationDashboardHttp.get<DedupPolicySummary>(
            `${DASH}/policy`,
        );
        return res.data;
    },

    async journeys(params: IdentityJourneyParams): Promise<IdentityJourneyResponse> {
        const res = await identityVerificationDashboardHttp.get<IdentityJourneyResponse>(
            `${DASH}/journeys`,
            { params },
        );
        return res.data;
    },

    async lookup(params: IdentityLookupParams): Promise<IdentityLookupResponse> {
        const res = await identityVerificationDashboardHttp.get<IdentityLookupResponse>(
            `${DASH}/lookup`,
            { params },
        );
        return res.data;
    },

    /**
     * Fetch raw bytes for an uploaded-image audit row. Returned as a Blob so
     * the caller can `URL.createObjectURL(blob)` for `<img src=…>` previews.
     * Throws if the row is S3-stored (501) or missing (404).
     */
    async uploadedImageContent(id: string): Promise<Blob> {
        const res = await identityVerificationDashboardHttp.get<Blob>(
            `${DASH}/uploaded-images/${encodeURIComponent(id)}/content`,
            { responseType: 'blob' },
        );
        return res.data;
    },

    /**
     * Fetch bytes by storage path (upload row lookup, else Azure blob key via shared-upload).
     * Used for reference selfies that only exist on customer_selfies.
     */
    async storageContentByPath(filePath: string): Promise<Blob> {
        const res = await identityVerificationDashboardHttp.get<Blob>(`${DASH}/storage-content`, {
            params: { file_path: filePath },
            responseType: 'blob',
        });
        return res.data;
    },

    /** Short-lived Azure SAS for an uploaded-image audit row. */
    async uploadedImageAccessUrl(id: string): Promise<StorageAccessUrlResponse> {
        const res = await identityVerificationDashboardHttp.get<StorageAccessUrlResponse>(
            `${DASH}/uploaded-images/${encodeURIComponent(id)}/access-url`,
        );
        return res.data;
    },

    /** Short-lived Azure SAS by blob path. */
    async storageAccessUrl(filePath: string): Promise<StorageAccessUrlResponse> {
        const res = await identityVerificationDashboardHttp.get<StorageAccessUrlResponse>(
            `${DASH}/storage-access-url`,
            { params: { file_path: filePath } },
        );
        return res.data;
    },
};
